'use client'

import React, { JSX, useEffect, useState } from 'react'
import { Box, Flex, Heading, Image, Text } from '@chakra-ui/react'
import { Article } from '@leid/api-articles'
import Link from 'next/link'

import { handleImageError } from 'utils/imageFallback'
import { normalizeUrl } from 'utils/normalizeUrl'
import { trimStringToLength } from 'utils/trimStringToLength'

type Props = {
  article: Article
}

const LimitedArticlePreview = ({ article }: Props): JSX.Element | null => {
  const defaultFallback = '/article-big.png'

  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined)
  const [errorLevel, setErrorLevel] = useState(0)

  useEffect(() => {
    if (!article) return

    const primary = article.imageVersions?.cdn?.thumbnail
    const fallback1 = article.image
    const fallback2 = defaultFallback

    let initialSrc = ''
    let initialErrorLevel = 0

    if (primary) {
      initialSrc = primary
      initialErrorLevel = 0
    } else if (fallback1) {
      initialSrc = fallback1
      initialErrorLevel = 1
    } else {
      initialSrc = fallback2
      initialErrorLevel = 2
    }

    setImgSrc((prev) => {
      if (prev === undefined || prev !== initialSrc) {
        setErrorLevel(initialErrorLevel)
        return initialSrc
      }
      return prev
    })
  }, [article])

  const handleImageErrorFallback = () => {
    handleImageError(defaultFallback, imgSrc, errorLevel, setImgSrc, setErrorLevel, article.image)
  }

  if (!article.body) {
    return null
  }

  return (
    <Link href={`/articles/${normalizeUrl(article.title ?? '', article.id)}`}>
      <Flex>
        <Image
          key={imgSrc}
          objectFit="cover"
          width="98px"
          height="98px"
          htmlWidth={'100px'}
          htmlHeight={'100px'}
          borderRadius="9px"
          src={imgSrc}
          onError={handleImageErrorFallback}
          alt=""
        />
        <Box marginLeft="20px">
          <Heading as="h3" fontSize="md" color="black" fontWeight={500} lineHeight="24px">
            {article.title}
          </Heading>
          <Text fontSize="sm" color="#414C49" marginTop="6px" lineHeight="18px" fontWeight={300}>
            {trimStringToLength(article.body.replace(/<[^>]*>/g, ' ').replace(/<img .*?>/g, ''), 75)}
          </Text>
        </Box>
      </Flex>
    </Link>
  )
}

export default LimitedArticlePreview
